import React, { useState } from 'react';
import Sidebar from '@/components/layout/Sidebar';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Award, Droplet } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const mockLeaderboardData = [
  { id: 1, name: 'Sarah Johnson', rank: 1, points: 1250, savings: '32%', badge: 'Water Wizard', avatar: 'SJ' },
  { id: 2, name: 'James Wilson', rank: 2, points: 1180, savings: '28%', badge: 'Conservation Champion', avatar: 'JW' },
  { id: 3, name: 'Emma Davis', rank: 3, points: 1050, savings: '25%', badge: 'Eco Warrior', avatar: 'ED' },
  { id: 4, name: 'Michael Brown', rank: 4, points: 980, savings: '23%', badge: 'Sustainability Star', avatar: 'MB' },
  { id: 5, name: 'Jessica Miller', rank: 5, points: 920, savings: '21%', badge: 'Water Saver', avatar: 'JM' },
  { id: 6, name: 'Christopher Garcia', rank: 6, points: 880, savings: '19%', badge: 'Eco Guardian', avatar: 'CG' },
  { id: 7, name: 'Ashley Martinez', rank: 7, points: 840, savings: '18%', badge: 'Conservation Hero', avatar: 'AM' },
  { id: 8, name: 'David Robinson', rank: 8, points: 790, savings: '17%', badge: 'Water Protector', avatar: 'DR' },
  { id: 9, name: 'Jennifer Lopez', rank: 9, points: 760, savings: '16%', badge: 'Resource Manager', avatar: 'JL' },
  { id: 10, name: 'Daniel Taylor', rank: 10, points: 730, savings: '15%', badge: 'Eco Enthusiast', avatar: 'DT' },
];

const mockFriendsData = [
  { id: 3, name: 'Emma Davis', rank: 1, points: 1050, savings: '25%', badge: 'Eco Warrior', avatar: 'ED' },
  { id: 7, name: 'Ashley Martinez', rank: 2, points: 840, savings: '18%', badge: 'Conservation Hero', avatar: 'AM' },
  { id: 10, name: 'Daniel Taylor', rank: 3, points: 730, savings: '15%', badge: 'Eco Enthusiast', avatar: 'DT' },
  { id: 12, name: 'Ryan Cooper', rank: 4, points: 680, savings: '14%', badge: 'Water Guardian', avatar: 'RC' },
  { id: 18, name: 'Lisa Chen', rank: 5, points: 590, savings: '12%', badge: 'Sustainability Ally', avatar: 'LC' },
];

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy className="h-6 w-6 text-yellow-500" />;
    case 2: 
      return <Medal className="h-6 w-6 text-gray-400" />;
    case 3:
      return <Medal className="h-6 w-6 text-amber-700" />;
    default:
      return <span className="text-sm font-medium text-muted-foreground w-6 text-center">{rank}</span>;
  }
};

const Leaderboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useIsMobile();
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  React.useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [isMobile]);
  
  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      <Navbar 
        className="z-10"
        onToggleSidebar={toggleSidebar}
      />
      
      <main className={`transition-all duration-300 p-4 sm:p-6 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Water Saving Leaderboard</h1>
            <div className="mt-2 md:mt-0">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/30">
                <Droplet className="h-4 w-4 mr-1" />
                Your Rank: #7
              </Badge>
            </div>
          </div>
          
          <Tabs defaultValue="global" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="global">Global</TabsTrigger>
              <TabsTrigger value="friends">Friends</TabsTrigger>
              <TabsTrigger value="neighborhood">Neighborhood</TabsTrigger>
            </TabsList>
            
            <TabsContent value="global">
              <Card>
                <CardHeader>
                  <CardTitle>Global Leaderboard</CardTitle>
                  <CardDescription>Top water savers from around the world</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Rank</th>
                          <th className="text-left py-3 px-2">User</th>
                          <th className="text-left py-3 px-2 hidden sm:table-cell">Badge</th>
                          <th className="text-right py-3 px-2">Points</th>
                          <th className="text-right py-3 px-2 hidden sm:table-cell">Water Saved</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {mockLeaderboardData.map((user) => (
                          <tr key={user.id} className="hover:bg-muted/50 transition-colors">
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-center">
                                {getRankIcon(user.rank)}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center">
                                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium mr-3">
                                  {user.avatar}
                                </div>
                                <span className="font-medium">{user.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 hidden sm:table-cell">
                              <div className="flex items-center">
                                <Award className="h-4 w-4 mr-1 text-amber-500" />
                                <span className="text-sm">{user.badge}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right font-medium">{user.points.toLocaleString()}</td>
                            <td className="py-3 px-2 text-right hidden sm:table-cell">
                              <span className="text-secondary">{user.savings}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="friends">
              <Card>
                <CardHeader>
                  <CardTitle>Friends Leaderboard</CardTitle>
                  <CardDescription>See how you rank among your friends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Rank</th>
                          <th className="text-left py-3 px-2">User</th>
                          <th className="text-left py-3 px-2 hidden sm:table-cell">Badge</th>
                          <th className="text-right py-3 px-2">Points</th>
                          <th className="text-right py-3 px-2 hidden sm:table-cell">Water Saved</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {mockFriendsData.map((user, index) => (
                          <tr key={user.id} className="hover:bg-muted/50 transition-colors">
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-center">
                                {getRankIcon(user.rank)}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center">
                                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium mr-3">
                                  {user.avatar}
                                </div>
                                <span className="font-medium">{user.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 hidden sm:table-cell">
                              <div className="flex items-center">
                                <Award className="h-4 w-4 mr-1 text-amber-500" />
                                <span className="text-sm">{user.badge}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right font-medium">{user.points.toLocaleString()}</td>
                            <td className="py-3 px-2 text-right hidden sm:table-cell">
                              <span className="text-secondary">{user.savings}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="neighborhood">
              <Card className="w-full h-64 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-muted-foreground mb-2">Neighborhood data coming soon</p>
                  <p className="text-sm">We're working on connecting with local water authorities</p>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default Leaderboard;
